import { Dictionary } from '@ngrx/entity';
import { createSelector, MemoizedSelector } from '@ngrx/store';

import { getCandidatesEntities } from '@collections/candidates';
import { hasAnonymization } from '@collections/users';
import { HiringManagerCandidatesState } from '@modules/job-page/store/hiring-manager';
import { Candidate, CandidateAdapter } from '@shared/models';
import { Pagination, PaginationAdapter } from '@shared/pagination';

import { getJobPageState, JobPageState } from './job.reducer';

export const getJobPageCandidatesState: MemoizedSelector<
  JobPageState,
  Pagination<Candidate>
> = createSelector(
  getJobPageState,
  (state: JobPageState) => !!state && state.totalMatchesCandidates,
);

export const getJobPageCandidatesIds = createSelector(
  getJobPageCandidatesState,
  (pagination: Pagination) => !!pagination && new PaginationAdapter(pagination).contentIds,
);

export const getJobPageCandidatesTotal = createSelector(
  getJobPageCandidatesState,
  (pagination: Pagination) => (!!pagination ? pagination.totalElements : 0),
);

export const getJobPageCanidatesIsLoading = createSelector(
  getJobPageCandidatesState,
  (pagination: Pagination) => pagination.loading,
);

export const getJobPageCandidates = (anonymize: boolean) =>
  createSelector(
    getJobPageCandidatesIds,
    getCandidatesEntities,
    hasAnonymization,
    (ids, entities, anonymization: boolean) =>
      !!ids &&
      ids.map((id: number) => new CandidateAdapter(entities[id], anonymize && !!anonymization)),
  );

export const getHiringManagerCandidatesState = createSelector(
  getJobPageState,
  (state: JobPageState) => !!state && state.hiringManagerCandidates,
);

export const getHiringManagerCandidatesTotals = createSelector(
  getHiringManagerCandidatesState,
  (state: HiringManagerCandidatesState) => !!state && state.totals,
);

export const getHiringManagerCandidatesIsLoading = createSelector(
  getHiringManagerCandidatesState,
  (state: HiringManagerCandidatesState) => !!state && state.loaded === false,
);

// in review :
export const getHiringManagerInReviewCandidates = createSelector(
  getHiringManagerCandidatesState,
  (state: HiringManagerCandidatesState) => !!state && state.inReviewCandidates,
);

export const getHiringManagerInReviewCandidatesPageNumber = createSelector(
  getHiringManagerInReviewCandidates,
  (pagination: Pagination) => !!pagination && pagination.number,
);

export const getHiringManagerInReviewCandidatesPage = createSelector(
  getHiringManagerInReviewCandidates,
  getCandidatesEntities,
  hasAnonymization,
  createCandidatesPage,
);

// advanced :
export const getHiringManagerAdvancedCandidates = createSelector(
  getHiringManagerCandidatesState,
  (state: HiringManagerCandidatesState) => !!state && state.advancedCandidates,
);

export const getHiringManagerAdvancedCandidatesPageNumber = createSelector(
  getHiringManagerAdvancedCandidates,
  (pagination: Pagination) => !!pagination && pagination.number,
);

export const getHiringManagerAdvancedCandidatesPage = createSelector(
  getHiringManagerAdvancedCandidates,
  getCandidatesEntities,
  hasAnonymization,
  createCandidatesPage,
);

// rejected :
export const getHiringManagerRejectedCandidates = createSelector(
  getHiringManagerCandidatesState,
  (state: HiringManagerCandidatesState) => !!state && state.rejectedCandidates,
);

export const getHiringManagerRejectedCandidatesPageNumber = createSelector(
  getHiringManagerRejectedCandidates,
  (pagination: Pagination) => !!pagination && pagination.number,
);

export const getHiringManagerRejectedCandidatesPage = createSelector(
  getHiringManagerRejectedCandidates,
  getCandidatesEntities,
  hasAnonymization,
  createCandidatesPage,
);

export const getMatchesNumbers = createSelector(
  getJobPageState,
  (state: JobPageState) => !!state && state.matchesNumbers,
);

export const getMatchesNumbersCombined = createSelector(
  getJobPageState,
  (state: JobPageState) =>
    !!state && state.matchesNumbers.inReviewDirect + state.matchesNumbers.passive,
);

export const getDirectApplicantsPerPhaseNumbers = createSelector(
  getJobPageState,
  (state: JobPageState) => state.directApplicantsPerPhaseNumber,
);

function createCandidatesPage(
  page: Pagination,
  candidateEntities: Dictionary<Candidate>,
  anonymization: boolean,
) {
  return (
    !!page && {
      ...page,
      content: !!page.content
        ? page.content.map(
            (candidate: CandidateAdapter) =>
              new CandidateAdapter(candidateEntities[candidate.id], anonymization),
          )
        : null,
    }
  );
}
